import { useState } from 'react';
import { useModal } from '../../../hooks/useModal';
import styled from 'styled-components';
import { Spinner } from '../../../components/Spinner/Spinner';
import { StyledLabel } from '../../../components/Input';
import { useGetPauseDatesQuery, usePauseSubscriptionMutation } from '../api/subscriptionApi';
import { toast } from 'react-toastify';
import { PausedDatesResponse } from '../api/types';
import { Button } from '../../../components/Button';

type PauseSubscriptionModalProps = {
  stripeId: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onPauseSuccess?: () => void; // Add this optional callback
};

export const usePauseSubscriptionModal = ({
  stripeId,
  isOpen,
  setIsOpen,
  onPauseSuccess
}: PauseSubscriptionModalProps) => {
  const { data: pausedDates, isLoading: loadingPauseDates } = useGetPauseDatesQuery(
    { customerId: stripeId || '' },
    { skip: !isOpen }
  );

  const { ModalComponent } = useModal({
    isOpen,
    onClose: () => setIsOpen(false),
    title: 'Pause subscription'
  });

  const PauseSubscriptionModal = (
    <ModalComponent>
      {loadingPauseDates ? (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      ) : (
        <ModalBody onClose={() => setIsOpen(false)} pausedDates={pausedDates} onPauseSuccess={onPauseSuccess} />
      )}
    </ModalComponent>
  );

  return { PauseSubscriptionModal };
};

type ModalBodyProps = {
  onClose: () => void;
  pausedDates?: PausedDatesResponse;
  onPauseSuccess?: () => void;
};

const PAUSE_REASONS = [
  { value: 'seasonal', label: 'Seasonal use only' },
  { value: 'no_security_needed', label: "Don't need security right now" }
];

const formatResumeLabel = (dateString: string) => {
  const parts = dateString.split('-');
  if (parts.length !== 3) return dateString;
  const monthIndex = Number(parts[1]) - 1;
  const year = parts[2];
  const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthLabels[monthIndex] || parts[1];
  return `${month} ${year.slice(-2)}`;
};

const ModalBody = ({ pausedDates, onClose, onPauseSuccess }: ModalBodyProps) => {
  const [pauseSubscription, { isLoading: loadingPause }] = usePauseSubscriptionMutation();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedResumeDate, setSelectedResumeDate] = useState<string>('');
  const [selectedReason, setSelectedReason] = useState<{ value: string; label: string }[]>([]);
  const resumeDates = pausedDates?.pause_options.resume_dates || [];

  if (!pausedDates) return null;

  const onPause = async () => {
    if (!selectedResumeDate) return;
    
    try {
      // Wait for the API call to complete
      await pauseSubscription({
        customerId: pausedDates.pause_options.customer_id,
        pauseDate: selectedResumeDate,
        reason: selectedReason[0]?.value || ''
      }).unwrap(); // Using unwrap() to ensure the promise is resolved properly
      
      // Close modal
      onClose();
      
      // Show success message
      toast.success('Subscription paused successfully');
      
      // Add a small delay to ensure backend has processed the change
      setTimeout(() => {
        // Call the success callback if provided
        if (onPauseSuccess) {
          onPauseSuccess();
        }
      }, 1000); // 1 second delay to ensure server has processed the pause
      
    } catch (error) {
      toast('Failed to pause subscription. Try again later.', { type: 'error' });
      onClose();
      return error;
    }
  };

  return (
    <ModalWrapper>
      {showConfirmation ? (
        <ModalInnerWrapper>
          <ModalTextWrapper>
            <p>Are you sure you want to pause your subscription?</p>
            <p>Your SIM will cease to be active until the stated date.</p>
          </ModalTextWrapper>
          <BtnWrapper>
            <Button
              isLoading={loadingPause}
              variant="danger"
              style={{ width: '100%' }}
              onClick={onPause}>
              Yes, I'm sure.
            </Button>
            <Button style={{ width: '100%' }} variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </BtnWrapper>
        </ModalInnerWrapper>
      ) : (
        <ModalInnerWrapper>
          <ModalTextWrapper>
            <p>{pausedDates?.pause_options.message}</p>
            <p>
              Pause from:{' '}
              <span style={{ fontWeight: 'bold' }}>{pausedDates?.pause_options.pause_from}</span>
            </p>
          </ModalTextWrapper>
          <div>
            <StyledLabel>Please select reactivation month (reactivates on the 1st):</StyledLabel>
            <DateButtonGroup>
              {resumeDates.map((dateOption) => (
                <DateButton
                  key={dateOption}
                  isSelected={selectedResumeDate === dateOption}
                  onClick={() => setSelectedResumeDate(dateOption)}>
                  {formatResumeLabel(dateOption)}
                </DateButton>
              ))}
            </DateButtonGroup>
          </div>
          <div>
            <StyledLabel>Reason for pausing:</StyledLabel>
            <ReasonButtonGroup>
              {PAUSE_REASONS.map((reason) => (
                <ReasonButton
                  key={reason.value}
                  isSelected={selectedReason[0]?.value === reason.value}
                  onClick={() => setSelectedReason([reason])}
                >
                  {reason.label}
                </ReasonButton>
              ))}
            </ReasonButtonGroup>
          </div>
          <BtnWrapper>
            <Button
              disabled={!selectedResumeDate || !selectedReason[0]?.value}
              style={{ width: '100%' }}
              onClick={() => setShowConfirmation(true)}>
              Pause
            </Button>
            <Button style={{ width: '100%' }} variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </BtnWrapper>
        </ModalInnerWrapper>
      )}
    </ModalWrapper>
  );
};

const BtnWrapper = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
  margin-top: 2rem;
`;

const ModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  height: 100%;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const ModalTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ModalInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: space-between;
  height: 100%;
`;

const ReasonButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const DateButtonGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.6rem;
`;

const DateButton = styled.button<{ isSelected: boolean }>`
  padding: 0.65rem 0.5rem;
  border: 2px solid ${(props) => (props.isSelected ? '#29a8d3' : '#e0e0e0')};
  background-color: ${(props) => (props.isSelected ? '#E6F7FF' : '#ffffff')};
  border-radius: 0.4rem;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  text-align: center;

  &:hover {
    border-color: #29a8d3;
    background-color: #f0fbff;
  }
`;

const ReasonButton = styled.button<{ isSelected: boolean }>`
  padding: 0.75rem 1rem;
  border: 2px solid ${(props) => (props.isSelected ? '#29a8d3' : '#e0e0e0')};
  background-color: ${(props) => (props.isSelected ? '#E6F7FF' : '#ffffff')};
  border-radius: 0.4rem;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    border-color: #29a8d3;
    background-color: #f0fbff;
  }

  &:active {
    background-color: ${(props) => (props.isSelected ? '#E6F7FF' : '#f5f5f5')};
  }
`;
